import { Notes as NotesIcon } from "@mui/icons-material";
import { Box, FormControlLabel, Switch, TextField } from "@mui/material";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AdminDialog } from "@/components/common";
import { CustomerNote, CustomerService } from "@/services/features/CustomerService";

interface CustomerNoteEditDialogProps {
  note: CustomerNote | null;
  onClose: () => void;
  onSuccess: (note: CustomerNote) => void;
}

export const CustomerNoteEditDialog: React.FC<CustomerNoteEditDialogProps> = ({
  note,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const customerService = CustomerService.getInstance();
  const [editedNote, setEditedNote] = useState<CustomerNote | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState({ title: false, content: false });
  const [isSaving, setIsSaving] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const contentInputRef = useRef<HTMLInputElement>(null);
  const displayedNote = note && (editedNote?.id === note.id ? editedNote : note);

  const closeDialog = () => {
    setEditedNote(null);
    setDialogError(null);
    setFieldErrors({ title: false, content: false });
    onClose();
  };

  const updateNote = (updates: Partial<CustomerNote>) => {
    if (!displayedNote) return;
    setEditedNote({ ...displayedNote, ...updates });
  };

  const handleSave = async () => {
    if (!displayedNote) return;
    setDialogError(null);
    const nextFieldErrors = {
      title: !displayedNote.title.trim(),
      content: !displayedNote.content.trim(),
    };
    setFieldErrors(nextFieldErrors);
    if (nextFieldErrors.title || nextFieldErrors.content) {
      (nextFieldErrors.title ? titleInputRef : contentInputRef).current?.focus();
      return;
    }

    setIsSaving(true);
    try {
      const response = await customerService.editCustomerNote(displayedNote);
      if (response.success && response.data) {
        onSuccess(response.data);
        closeDialog();
      } else {
        setDialogError(
          response.error || t("management.customers.editCustomerNote.errors.failedUpdateNote"),
        );
      }
    } catch (error) {
      setDialogError(
        error instanceof Error
          ? error.message
          : t("management.customers.editCustomerNote.errors.errorUpdatingNote"),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0]) return;
    setDialogError(null);
    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append("file", event.target.files[0]);
      const response = await customerService.uploadFile(formData);
      if (response.success && response.data) {
        updateNote({ fileUrl: response.data });
      } else {
        setDialogError(
          response.error || t("management.customers.editCustomerNote.errors.failedUploadFile"),
        );
      }
    } catch (error) {
      setDialogError(
        error instanceof Error
          ? error.message
          : t("management.customers.editCustomerNote.errors.errorUploadingFile"),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AdminDialog
      open={Boolean(displayedNote)}
      onClose={closeDialog}
      maxWidth="sm"
      title={t("management.customers.editCustomerNote.title")}
      icon={<NotesIcon />}
      error={dialogError}
      errorOperation="update"
      errorEntity={t("common.entities.note")}
      cancelAction={{ label: t("common.cancel"), onClick: closeDialog, disabled: isSaving }}
      primaryAction={{
        label: isSaving
          ? t("management.customers.editCustomerNote.actions.saving")
          : t("management.customers.editCustomerNote.actions.save"),
        onClick: () => void handleSave(),
        loading: isSaving,
      }}
    >
      {displayedNote && (
        <>
          <TextField
            fullWidth
            required
            margin="normal"
            label={t("management.customers.customerNotes.fields.title")}
            value={displayedNote.title}
            inputRef={titleInputRef}
            error={fieldErrors.title}
            helperText={fieldErrors.title ? t("common.required") : undefined}
            disabled={isSaving}
            onChange={(event) => {
              updateNote({ title: event.target.value });
              if (event.target.value.trim())
                setFieldErrors((current) => ({ ...current, title: false }));
            }}
          />
          <TextField
            fullWidth
            required
            margin="normal"
            multiline
            rows={5}
            label={t("management.customers.customerNotes.fields.content")}
            value={displayedNote.content}
            inputRef={contentInputRef}
            error={fieldErrors.content}
            helperText={fieldErrors.content ? t("common.required") : undefined}
            disabled={isSaving}
            onChange={(event) => {
              updateNote({ content: event.target.value });
              if (event.target.value.trim())
                setFieldErrors((current) => ({ ...current, content: false }));
            }}
          />
          <Box sx={{ mt: 2 }}>
            <input
              type="file"
              disabled={isSaving}
              onChange={(event) => void handleFileUpload(event)}
            />
            <TextField
              fullWidth
              margin="normal"
              label={t("management.customers.customerNotes.fields.fileUrl")}
              value={displayedNote.fileUrl || ""}
              disabled={isSaving}
              onChange={(event) => updateNote({ fileUrl: event.target.value })}
            />
          </Box>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={displayedNote.archived}
                disabled={isSaving}
                onChange={(event) => updateNote({ archived: event.target.checked })}
              />
            }
            label={t("management.customers.customerNotes.fields.archived")}
          />
        </>
      )}
    </AdminDialog>
  );
};
